import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, HelpCircle } from "lucide-react";
import { useAuth, apiCall } from "../App";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";

const ProfilingQuiz = () => {
  const { token, updateUser } = useAuth();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchQuestions = async () => {
      const result = await apiCall("GET", "/profiling/questions", null, token);
      if (result.success) {
        setQuestions(result.data.questions);
      }
      setLoading(false);
    };
    fetchQuestions();
  }, [token]);

  const currentQuestion = questions[currentIndex];
  const progress = questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0;

  const updateAnswer = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    // Format answers for API. Trigger questions store structured {selected, text}
    // — combine into a single string for backwards-compat with NLP keyword matcher.
    const serializeTriggerAnswer = (v) => {
      if (!v) return "";
      if (typeof v === "string") return v;
      if (typeof v === "object" && (Array.isArray(v.selected) || typeof v.text === "string")) {
        const parts = [...(v.selected || []), v.text || ""].filter(s => s && s.trim());
        return parts.join(" • ");
      }
      return v;
    };

    const formattedAnswers = Object.entries(answers).map(([questionId, value]) => {
      // Structured trigger answer
      if (value && typeof value === "object" && (Array.isArray(value.selected) || typeof value.text === "string")) {
        return {
          question_id: questionId,
          answer_text: serializeTriggerAnswer(value),
          answer_options: { selected: value.selected || [], text: value.text || "" },
        };
      }
      if (typeof value === "string") {
        return { question_id: questionId, answer_text: value };
      } else if (typeof value === "object") {
        return { question_id: questionId, answer_options: value };
      } else {
        return { question_id: questionId, answer_value: value };
      }
    });

    const result = await apiCall("POST", "/profiling/submit", { answers: formattedAnswers }, token);
    setSubmitting(false);

    if (result.success) {
      updateUser({ profiling_complete: true });
      toast.success("12D Profiling Complete! Welcome to your 3A Dashboard.");
      navigate("/dashboard");
    } else {
      toast.error(result.error);
    }
  };

  // Read the structured shape of a trigger answer ({selected, text}) — supports legacy string state
  const getTriggerState = (q) => {
    const raw = answers[q.id];
    if (raw && typeof raw === "object" && (Array.isArray(raw.selected) || typeof raw.text === "string")) {
      return { selected: raw.selected || [], text: raw.text || "" };
    }
    if (typeof raw === "string") {
      // Legacy: treat the whole thing as free text
      return { selected: [], text: raw };
    }
    return { selected: [], text: "" };
  };

  const renderTextQuestion = (q) => {
    const hasSuggestions = Array.isArray(q.suggestions) && q.suggestions.length > 0;
    const { selected, text } = getTriggerState(q);
    const charLimit = q.max_chars || 100;

    const toggleChip = (chip) => {
      const next = selected.includes(chip)
        ? selected.filter(s => s !== chip)
        : [...selected, chip];
      updateAnswer(q.id, { selected: next, text });
    };

    return (
      <div className="space-y-4">
        {hasSuggestions && (
          <>
            <p className="text-chakra-crown/60 text-xs leading-relaxed" data-testid={`trigger-instruction-${q.id}`}>
              Select all that apply <span className="text-divine-gold/80">and/or</span> describe in your own words below. ({selected.length} selected)
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2" data-testid={`trigger-chips-${q.id}`}>
              {q.suggestions.map((s, i) => {
                const isOn = selected.includes(s);
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => toggleChip(s)}
                    data-testid={`trigger-chip-${q.id}-${i}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-left text-xs transition-all ${
                      isOn
                        ? "border-divine-gold bg-divine-gold/15 text-divine-gold"
                        : "border-divine-gold/20 bg-cosmic-light/5 text-chakra-crown/70 hover:border-divine-gold/40 hover:text-chakra-crown"
                    }`}
                  >
                    <span className={`shrink-0 w-3.5 h-3.5 rounded border-[1.5px] flex items-center justify-center ${
                      isOn ? "border-divine-gold bg-divine-gold/20" : "border-divine-gold/40"
                    }`}>
                      {isOn && <span className="block w-1.5 h-1.5 rounded-sm bg-divine-gold" />}
                    </span>
                    <span className="leading-snug">{s}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
        <div>
          {hasSuggestions && (
            <p className="text-chakra-crown/40 text-[10px] mb-1.5 uppercase tracking-wider">Your own words (optional)</p>
          )}
          <Textarea
            value={text}
            onChange={(e) => updateAnswer(q.id, { selected, text: e.target.value })}
            placeholder={hasSuggestions ? "Describe in your own words..." : "Share your experience..."}
            className="min-h-[100px] bg-neutral-obsidian/50 border border-divine-gold/20 focus:border-divine-gold text-chakra-crown placeholder:text-divine-gold/30 resize-none"
            maxLength={charLimit}
            data-testid={`answer-${q.id}`}
          />
          <div className="flex justify-end text-sm mt-1">
            <span className="text-chakra-crown/40">{(text || "").length}/{charLimit}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderScaleQuestion = (q) => (
    <div className="space-y-6">
      {q.scale_items?.map((item) => (
        <div key={item} className="space-y-2">
          <div className="flex justify-between">
            <span className="text-chakra-crown capitalize">{item.replace("_", " ")}</span>
            <span className="text-divine-gold">{answers[q.id]?.[item] || 0}</span>
          </div>
          <Slider
            value={[answers[q.id]?.[item] || 0]}
            onValueChange={([val]) => {
              updateAnswer(q.id, { ...answers[q.id], [item]: val });
            }}
            max={5}
            step={1}
            className="[&_[role=slider]]:bg-divine-gold [&_[role=slider]]:border-divine-gold"
            data-testid={`slider-${item}`}
          />
        </div>
      ))}
      <p className="text-xs text-chakra-crown/50 text-center">
        {q.scale_labels?.join(" | ")}
      </p>
    </div>
  );

  const renderDualScaleQuestion = (q) => (
    <div className="space-y-8">
      {/* Masculine Energy */}
      <div>
        <h4 className="text-divine-gold font-medium mb-4 text-center">♂ Your Masculine Energy</h4>
        <div className="space-y-4">
          {q.masculine_items?.map((item) => (
            <div key={item.key} className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-chakra-crown">{item.label}</span>
                <span className="text-divine-gold">{answers[q.id]?.masculine?.[item.key] || 0}</span>
              </div>
              <Slider
                value={[answers[q.id]?.masculine?.[item.key] || 0]}
                onValueChange={([val]) => {
                  updateAnswer(q.id, {
                    ...answers[q.id],
                    masculine: { ...(answers[q.id]?.masculine || {}), [item.key]: val }
                  });
                }}
                max={5}
                step={1}
                className="[&_[role=slider]]:bg-divine-gold"
                data-testid={`slider-masc-${item.key}`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Feminine Energy */}
      <div>
        <h4 className="text-chakra-crown font-medium mb-4 text-center">♀ Your Feminine Energy</h4>
        <div className="space-y-4">
          {q.feminine_items?.map((item) => (
            <div key={item.key} className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-chakra-crown">{item.label}</span>
                <span className="text-chakra-crown">{answers[q.id]?.feminine?.[item.key] || 0}</span>
              </div>
              <Slider
                value={[answers[q.id]?.feminine?.[item.key] || 0]}
                onValueChange={([val]) => {
                  updateAnswer(q.id, {
                    ...answers[q.id],
                    feminine: { ...(answers[q.id]?.feminine || {}), [item.key]: val }
                  });
                }}
                max={5}
                step={1}
                className="[&_[role=slider]]:bg-chakra-crown"
                data-testid={`slider-fem-${item.key}`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-cosmic-void flex items-center justify-center">
        <div className="text-divine-gold animate-pulse font-heading text-2xl">
          Loading 12D Profiling Engine...
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-cosmic-void py-12 px-6"
      data-testid="profiling-page"
    >
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl text-divine-gold tracking-wider">
            12D PROFILING ENGINE
          </h1>
          <p className="text-chakra-crown/60 mt-2">
            Sambandha Jnana - Awareness Assessment
          </p>
        </div>

        {/* Progress Circle */}
        <div className="flex justify-center mb-8">
          <div className="relative w-24 h-24">
            <svg className="w-full h-full transform -rotate-90">
              <circle
                cx="48"
                cy="48"
                r="40"
                stroke="#240046"
                strokeWidth="8"
                fill="none"
              />
              <circle
                cx="48"
                cy="48"
                r="40"
                stroke="#ffd700"
                strokeWidth="8"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 40}`}
                strokeDashoffset={`${2 * Math.PI * 40 * (1 - progress / 100)}`}
                className="transition-all duration-500"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-divine-gold font-heading text-lg">
                {currentIndex + 1}/{questions.length}
              </span>
            </div>
          </div>
        </div>

        {/* Question Card */}
        {currentQuestion && (
          <motion.div
            key={currentQuestion.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="sacred-card"
            data-testid="question-card"
          >
            {/* Pillar Badge */}
            <div className="flex items-center justify-between mb-4">
              <span className="label-sacred">
                {currentQuestion.pillar}
              </span>
              <span className="text-chakra-crown/60 text-sm">
                {currentQuestion.pillar_english}
              </span>
            </div>

            {/* Question */}
            <h2 className="font-heading text-xl text-divine-gold mb-6" data-testid="question-text">
              {currentQuestion.question}
            </h2>

            {/* Answer Input */}
            {currentQuestion.type === "text" && renderTextQuestion(currentQuestion)}
            {currentQuestion.type === "scale" && renderScaleQuestion(currentQuestion)}
            {currentQuestion.type === "dual_scale" && renderDualScaleQuestion(currentQuestion)}
            {currentQuestion.type === "text_with_dropdown" && renderTextQuestion(currentQuestion)}
            {currentQuestion.type === "dropdown_multi" && renderTextQuestion(currentQuestion)}

            {/* Empathy Message */}
            {currentQuestion.empathy && (
              <div className="mt-6 p-4 rounded-xl bg-cosmic-light/20 border border-divine-gold/10">
                <p className="text-chakra-crown/70 text-sm italic flex items-start gap-2">
                  <span className="text-lg">💙</span>
                  {currentQuestion.empathy}
                </p>
              </div>
            )}
          </motion.div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={currentIndex === 0}
            className="text-chakra-crown hover:text-divine-gold"
            data-testid="prev-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>

          {currentIndex < questions.length - 1 ? (
            <Button
              onClick={handleNext}
              className="vedic-button"
              data-testid="next-btn"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="vedic-button"
              data-testid="submit-btn"
            >
              {submitting ? "Calculating..." : "Complete Assessment"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilingQuiz;
